/* eslint-disable @typescript-eslint/no-unused-vars */
import { intersection, orderBy } from "lodash";
import moment from "moment";

import { base64, base64url } from "js/utils/encoding";
import * as chatApi from "js/api/chatApi";
import * as usersApi from "js/api/usersApi";
import { v4 as uuidV4 } from "uuid";
import { isMembershipOnly } from "./classwizeConfigStore";
import useStore from "stores/index";
import { useUserSessionStore } from "./v2/userSessionStore";
import { envConfig } from "@/config";

const CHAT_LOAD_MSG_COUNT = envConfig.CHAT_LOAD_MSG_COUNT || 15;

export const createChatStore = (set, get, api, setIn) => ({
	chatWindowOpen: false,
	chatWindowMinimized: false,
	chatToggleModalOpen: false,
	selectedClassId: "",
	selectedStudentName: "",
	selectedThreadKey: "",
	isSendingMessage: false,
	/** When true, nav should open Account Settings (chat username prompt from opening chat without a name). */
	accountSettingsOpenForChatUsername: false,
	refreshNotificationsStatus: false,
	chatError: null,

	// diffentiate user scroll initiatied fetch
	scrollFetch: false,

	unreadMessagesCount: {
		isFetching: false,
		data: {},
		error: null,
		lastFetched: 0,
	},
	classroomOverview: {
		isFetching: false,
		data: [],
		error: null,
		lastFetched: 0,
	},
	threads: {
		isFetching: false,
		data: {},
		error: null,
		lastFetched: 0,
		isUpdateFetch: false,
	},
	displayName: {
		isFetching: false,
		isSubmittingDisplayName: false,
		data: "",
		error: null,
	},
	messageListener: {
		isFetching: false,
		error: null,
		isActive: false,
	},
	markThreadAsRead: {
		isFetching: false,
		error: null,
	},
	avatarColors: ["#973AA8", "#FF9900", "#29D9C2", "#16C7FF", "#FF949B"],
	// thread teachers is used exclusively to determine thread key for membership only cw config and
	// for synced e.g. google groups used as classrooms
	threadTeachers: {},

	/**
	 * Generates a unique thread key for a given classroom object and student object.
	 * There was a bug caused by the misuse of user objects vs username strings, therefore I added JSDoc to clarify the types.
	 * @param {ClassroomDetails} classroom - classroom object
	 * @param {UserInfo} student - student object
	 * @returns {string | undefined} thread key
	 */
	getThreadKey: (classroom, student) => {
		const teacherStableId = useUserSessionStore.getState().getTeacherId();
		const studentStableId = student.stableIdV1;
		const classroomStableId = classroom.stableIdV1;

		if (!teacherStableId || !studentStableId || !classroomStableId) {
			// Reviewed and refactored all the usages of getThreadKey
			// It should now always have access to teacherStableId, studentStableId, classroomStableId, unless the backend sends incorrect data
			// A possible scenario of hitting this line is, CW receives a student with undefined stableId
			console.error("Error generating thread key");
			return;
		}

		return base64(`${teacherStableId},${studentStableId},${classroomStableId}`);
	},

	fetchThreadTeachers: async (classroom) => {
		let threadTeachers = [];

		// when cw config is not membership or when classroom is not a synced group
		// we dont need to figure out the teachers for the thread key
		if (!useStore.getState().classroom.isClassSyncGroup(classroom) || !isMembershipOnly(useStore.getState())) {
			return;
		}

		const name = classroom.sourceId;

		// dont fetch if thread teachers list already exists
		if (get().chat.threadTeachers[name]) {
			return;
		}

		if (name === localStorage.getItem("ACTIVE_GROUP")) {
			const teachers = useStore.getState().classroomUsers.exceptionUsers;
			if (teachers) {
				const tempThreadTeachers = get().chat.threadTeachers;
				tempThreadTeachers[name] = teachers;
				setIn({ threadTeachers: tempThreadTeachers });
				return;
			}
		}

		const users = await usersApi.getClassroomUsers(name);

		// exception users in a classroom group are teachers !
		threadTeachers = users["excluded_users"].map((u) => u.username).filter((user) => user);

		const tempThreadTeachers = get().chat.threadTeachers;
		tempThreadTeachers[name] = threadTeachers;

		setIn({ threadTeachers: tempThreadTeachers });
		return;
	},

	setSelectedThreadKey: (newThreadKey) => {
		setIn({ selectedThreadKey: newThreadKey });
	},

	setSelectedClassId: (newSelectedClassId) => {
		setIn({ selectedClassId: newSelectedClassId });
	},

	setSelectedStudentName: (newSelectedStudentName) => {
		setIn({ selectedStudentName: newSelectedStudentName });
	},

	setThreadDetails: ({ selectedThreadKey, selectedClassId, selectedStudentName }) => {
		setIn({
			selectedThreadKey,
			selectedClassId,
			selectedStudentName,
		});
	},

	openChatEnableModal: (status) => {
		setIn({ chatToggleModalOpen: status });
	},

	toggleChatWindow: (forceClose) => {
		const shouldOpen = !get().chat.chatWindowOpen;
		if (forceClose) {
			setIn({
				selectedThreadKey: "",
				selectedStudentName: "",
				chatWindowOpen: false,
			});
			return;
		}

		// Open Account Settings (chat username) if there is no display name — same UX as legacy DisplayNameModal
		if (shouldOpen && !get().chat.displayName.data && !get().chat.displayName.error) {
			setIn({ accountSettingsOpenForChatUsername: true });
		}

		// if being closed and not on student list, go to student list
		if (!shouldOpen && get().chat.selectedThreadKey) {
			setIn({ selectedThreadKey: "", selectedStudentName: "" });
		} else {
			setIn({ chatWindowOpen: shouldOpen });
		}
	},
	dismissAccountSettingsChatPrompt: () => {
		const currentDisplayName = get().chat.displayName;
		setIn({
			accountSettingsOpenForChatUsername: false,
			displayName: { ...currentDisplayName, error: null },
		});
	},

	fetchOverview: async () => {
		setIn({
			unreadMessagesCount: {
				...get().chat.unreadMessagesCount,
				isFetching: true,
			},
		});
		try {
			const data = await chatApi.getChatOverview(
				{},
				{
					username: localStorage.getItem("USERNAME"),
					applianceId: localStorage.getItem("DEVICE"),
				}
			);
			setIn({
				unreadMessagesCount: {
					data,
					isFetching: false,
					lastFetched: Date.now(),
					error: null,
				},
			});
		} catch (err) {
			console.log(err);
			setIn({
				unreadMessagesCount: {
					isFetching: false,
					lastFetched: null,
					error: err,
				},
			});
		}
	},

	fetchClassOverview: async ({ classroomId, ...params }) => {
		setIn({
			classroomOverview: {
				...get().chat.classroomOverview,
				isFetchingClassromOverview: true,
			},
		});

		const classroomOverview = await chatApi.getClassChatOverview(
			{},
			{
				classroomId: base64url(classroomId),
				username: localStorage.getItem("USERNAME"),
				applianceId: localStorage.getItem("DEVICE"),
				...params,
			}
		);

		const threads = {};
		Object.entries(classroomOverview).forEach(([threadKey, value]) => {
			threads[threadKey] = [value.latestMessage];
		});

		setIn({
			threads: {
				...get().chat.threads,
				data: {
					...get().chat.threads.data,
					...threads,
				},
			},
		});
	},

	fetchMessages: async ({ classroomId, ...params }, forceFetch = false, useIndexPagination = true, isUpdateFetch = false) => {
		if ((!params.threadKey || !get().chat.chatWindowOpen) && !forceFetch) {
			return;
		}

		if (!classroomId) {
			classroomId = get().chat.selectedClassId;
		}

		const threads = get().chat.threads.data;

		// start index must be always set at 0 minimum to get latest messages
		params["maxResults"] = params.maxResults || CHAT_LOAD_MSG_COUNT;
		params["sortDirection"] = "DESC";

		// use index pagination flag is to indicate if fetch is triggered by send message
		if (useIndexPagination && threads[params.threadKey] && threads[params.threadKey].length >= CHAT_LOAD_MSG_COUNT) {
			const latestMessageIndex = threads[params.threadKey].length - 1;
			params["startAfter"] = threads[params.threadKey][latestMessageIndex].timestamp;
		}

		setIn({
			threads: { ...get().chat.threads, isFetching: true, isUpdateFetch },
		});
		const messages = await chatApi.getMessages(
			{},
			{
				classroomId: base64url(classroomId),
				username: localStorage.getItem("USERNAME"),
				applianceId: localStorage.getItem("DEVICE"),
				...params,
			}
		);

		if (messages && threads[params.threadKey]) {
			let thread = threads[params.threadKey];
			messages.forEach((newMessage) => {
				const index = thread.findIndex((msg) => msg.id === newMessage.id);
				if (index !== -1) {
					thread[index] = newMessage;
				} else {
					thread.push(newMessage);
				}
			});
			thread = orderBy(thread, ["timestamp"], ["desc"]);
			threads[params.threadKey] = thread;
		}
		setIn({
			threads: {
				data: threads,
				isFetching: false,
				lastFetched: Date.now(),
				isUpdateFetch: false,
			},
		});
	},

	fetchDisplayName: async (params) => {
		const currentDisplayName = get().chat.displayName;
		setIn({ displayName: { ...currentDisplayName, isFetching: true } });
		try {
			const displayNameData = await chatApi.getDisplayName(
				{},
				{
					username: localStorage.getItem("USERNAME"),
					applianceId: localStorage.getItem("DEVICE"),
					...params,
				}
			);
			setIn({
				displayName: {
					...currentDisplayName,
					isFetching: false,
					data: displayNameData ? displayNameData.displayName : "",
				},
			});
		} catch (err) {
			setIn({
				displayName: {
					isFetching: false,
					data: "",
					error: true,
				},
				chatError: err.respJson.error,
			});
		}
	},

	toggleScrollFetch: (status) => {
		setIn({ scrollFetch: status });
	},

	createPlaceholderMessage: (message) => {
		const threads = get().chat.threads.data;
		if (!threads[message.threadKey]) {
			threads[message.threadKey] = [message];
		}
	},

	generateMessageId: () => uuidV4(),

	sendMessage: async ({ message, username, classroomId, threadKey, id, participants }, addToThread = true) => {
		const sender = localStorage.getItem("USERNAME");

		let fallbackTeachers = [];

		const teachers = get().chat.threadTeachers[classroomId];

		// use the teachers fetched for thread key if it exist
		// it means cw membership only and classroom is a synced group
		if (teachers && teachers.length) {
			fallbackTeachers = teachers;
		}

		const updatedParticipants = new Set([...participants, username, sender, ...fallbackTeachers]);

		const formattedMessage = {
			id,
			applianceId: localStorage.getItem("DEVICE"),
			message,
			classroomId: classroomId,
			sender: {
				displayName: get().chat.displayName.data,
				username: sender,
			},
			participants: [...updatedParticipants],
			threadKey,
			isSending: true,
		};

		if (addToThread) {
			const thread = get().chat.threads.data[threadKey];
			const threads = get().chat.threads;
			const newThread = thread ? [formattedMessage, ...thread] : [formattedMessage];

			setIn({
				threads: {
					...threads,
					data: {
						...threads.data,
						[threadKey]: newThread,
					},
				},
			});
		}
		try {
			await chatApi.sendMessage(formattedMessage);

			const thread = get().chat.threads.data[threadKey];
			const threads = get().chat.threads;

			// update send status if successfully sent
			formattedMessage["isSending"] = false;
			// only required for currently sent messages as ts is generated from chat service
			formattedMessage.timestamp = new Date();
			const index = thread.findIndex((msg) => msg.id === id);
			thread[index] = formattedMessage;
			setIn({
				threads: {
					...threads,
					data: {
						...threads.data,
						[threadKey]: thread,
					},
				},
			});
			setIn({ isSendingMessage: false, sendMessageError: false });
		} catch (err) {
			setIn({ sendMessageError: true });
		}
	},

	validateDisplayNameInput: (displayName) => {
		const currentDisplayName = get().chat.displayName;

		if (!displayName) {
			setIn({
				displayName: {
					...currentDisplayName,
					error: true,
					errorMsg: "Display name must not be blank.",
				},
			});

			return false;
		}

		const trimDisplayName = displayName.trim();

		if (trimDisplayName.length > 80) {
			const excessLength = trimDisplayName.length - 80;

			setIn({
				displayName: {
					...currentDisplayName,
					error: true,
					errorMsg: `Your username is too long. Try removing ${excessLength} characters.`,
				},
			});

			return false;
		}

		return true;
	},

	submitDisplayName: async (displayName) => {
		const currentDisplayName = get().chat.displayName;

		if (!get().chat.validateDisplayNameInput(displayName)) {
			return false;
		}

		setIn({
			displayName: {
				...currentDisplayName,
				isSubmittingDisplayName: true,
				error: false,
				errorMsg: "",
			},
		});

		try {
			const displayNameData = await chatApi.saveDisplayName({
				displayName,
				username: localStorage.getItem("USERNAME"),
				applianceId: localStorage.getItem("DEVICE"),
			});

			setIn({
				displayName: {
					...currentDisplayName,
					isSubmittingDisplayName: false,
					data: displayNameData ? displayNameData.displayName : "",
					error: false,
					errorMsg: "",
				},
			});

			return true;
		} catch (err) {
			setIn({
				displayName: {
					...currentDisplayName,
					isSubmittingDisplayName: false,
					error: true,
					errorMsg: "",
				},
			});

			return false;
		}
	},

	markThreadAsReadCall: (threadKey, firstUnreadMessage) => {
		if (get().chat.chatError) {
			return;
		}
		setIn({
			markThreadAsRead: {
				isFetching: true,
				error: null,
			},
		});

		try {
			const thread = chatApi.markThreadAsRead({
				threadKey,
				username: localStorage.getItem("USERNAME"),
				applianceId: localStorage.getItem("DEVICE"),
				student: getSelectedStudentName(get()),
				firstUnreadMessageTime: firstUnreadMessage.timestamp,
			});
			const unreadMessagesCount = get().chat.unreadMessagesCount.data;

			try {
				unreadMessagesCount[threadKey].hasUnreadMessages = false;
			} catch (err) {
				// do nothing
			}
			setIn({
				markThreadAsRead: {
					isFetching: false,
					error: null,
				},
				threads: {
					...get().chat.threads,
					data: {
						...get().chat.threads.data,
						thread,
					},
				},
				unreadMessagesCount: {
					...get().chat.unreadMessagesCount,
					data: unreadMessagesCount,
				},
			});
		} catch (err) {
			setIn({
				markThreadAsRead: {
					isFetching: false,
					error: err,
				},
			});
		}
	},

	markClassroomThreadsAsRead: (classroomId) => {
		if (get().chat.chatError) {
			return;
		}
		setIn({
			markThreadAsRead: {
				isFetching: true,
				error: null,
			},
		});

		try {
			const thread = chatApi.markClassroomThreadsAsRead({
				classroomId,
				username: localStorage.getItem("USERNAME"),
				applianceId: localStorage.getItem("DEVICE"),
				student: getSelectedStudentName(get()),
			});
			const oldUnreadMessagesCount = get().chat.unreadMessagesCount.data;
			const unreadMessagesCount = Object.keys(oldUnreadMessagesCount).map((threadKey) => {
				const unreadMessageOverview = oldUnreadMessagesCount[threadKey];

				if (classroomId) {
					if (unreadMessageOverview.classroomId === classroomId) {
						return {
							...unreadMessageOverview,
							hasUnreadMessages: false,
						};
					}
				} else {
					return {
						...unreadMessageOverview,
						hasUnreadMessages: false,
					};
				}
				return unreadMessageOverview;
			});

			setIn({
				markThreadAsRead: {
					isFetching: false,
					error: null,
				},
				unreadMessagesCount: {
					...get().chat.unreadMessagesCount,
					data: unreadMessagesCount,
				},
			});
			if (classroomId) {
				setIn({
					threads: {
						...get().chat.threads,
						data: {
							...get().chat.threads.data,
							thread,
						},
					},
				});
			}
		} catch (err) {
			setIn({
				markThreadAsRead: {
					isFetching: false,
					error: err,
				},
			});
		}
	},

	updateChatOverviewData: (data) => {
		setIn({
			unreadMessagesCount: {
				...get().chat.unreadMessagesCount,
				data,
			},
		});
	},

	refreshNotifications: (status) => {
		setIn({ refreshNotificationsStatus: status });
	},
});

export const getChatOverview = (state) => (state.chat && state.chat.unreadMessagesCount ? state.chat.unreadMessagesCount.data : {});

/**
 * Returns the messages for a given classroom and students to be displayed in the ChatWindow.
 * There was a bug caused by the misuse of user objects vs username strings, therefore I added JSDoc to clarify the types.
 * @param state - zustand store
 * @param {Array<UserInfo>} users - student objects in the classroom
 * @param {ClassroomDetails} classroom - classroom object
 * @returns {*[] | undefined} messages (not yet typed)
 */
export const getMessagesForClassroomUsers = (state, users, classroom) => {
	if (!users || users.length === 0) {
		return [];
	}

	const threadKeys = Object.keys(state.chat.threads.data);
	if (!threadKeys) {
		return;
	}

	const messageThreadsForUsers = [];
	const usernames = users.map((s) => s.username);

	threadKeys.forEach((threadKey) => {
		if (!state.chat.threads.data[threadKey] || !state.chat.threads.data[threadKey][0]) {
			return;
		}
		const recipients = intersection(state.chat.threads.data[threadKey][0].participants, usernames);

		if (recipients.length) {
			messageThreadsForUsers.push({
				...state.chat.threads.data[threadKey][0],
				recipientUsername: recipients[0],
			});
			const recipientIndex = usernames.findIndex((username) => username === recipients[0]);
			if (recipientIndex !== -1) {
				usernames.splice(recipientIndex, 1);
			}
		}
	});

	usernames.forEach((username) => {
		const user = users.find((u) => u.username === username);

		const fakeMessage = {
			recipientUsername: username,
			threadKey: state.chat.getThreadKey(classroom, user),
			isFakeMessage: true,
			isRead: true,
		};

		state.chat.createPlaceholderMessage(fakeMessage);
		messageThreadsForUsers.push(fakeMessage);
	});

	return messageThreadsForUsers;
};

export const getMessagesForThreadKey = (state, threadKey) => {
	if (!threadKey) {
		return [];
	}
	if (threadKey === state.chat.selectedThreadKey) {
		const unreadMessages = state.chat.threads.data[threadKey].filter((msg) => !msg.isRead);
		if (unreadMessages.length) {
			const firstUnreadMessage = unreadMessages.sort((a, b) => new Date(a.timestamp).getTime() < new Date(b.timestamp).getTime())[0];
			state.chat.markThreadAsReadCall(threadKey, firstUnreadMessage);
		}
	}
	return state.chat.threads.data[threadKey];
};

export const getChatWindowOpen = (state) => state.chat.chatWindowOpen;

export const getSelectedClassId = (state) => state.chat.selectedClassId;

export const getSelectedStudentName = (state) => state.chat.selectedStudentName;

export const getSelectedThreadKey = (state) => state.chat.selectedThreadKey;

export function formatMsgDisplayTime(todayDate, msgDate) {
	const msgTs = moment(msgDate);

	if (msgTs.isSame(todayDate, "day")) {
		return `Today, ${msgTs.format("h:mm A")}`;
	}

	if (msgTs.isSame(todayDate, "week")) {
		return msgTs.format("ddd, h:mm A");
	}

	return msgTs.format("DD MMM YYYY, h:mm A");
}
